import { useCallback, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import AssistChip from 'LuxKit/Chips/AssistChip'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import useAddHotelToTrip from './useAddHotelToTrip'
import type { SendMessageOptions } from './useAddHotelToTrip'
import { showAddingToTripSnackbar } from 'tripPlanner/components/GenericDrawer/addToTripSnackbar'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import type { AssistantOccupancyAgeThresholds } from 'tripPlanner/assistant/utils/assistantOccupancyAgeThresholds'
import AddHotelToTripModal from './AddHotelToTripModal'
import noop from 'lib/function/noop'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'

interface Props {
  itineraryData?: TripItineraryData
  hotelName: string
  offerId: string
  offerType?: string
  hotelCity?: string
  contextStop?: TripStop
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  variant?: 'filled' | 'outline'
  size?: 'small' | 'medium'
  fit?: 'auto' | 'full-width'
  onAfterAction?: () => void
  filterNights?: number
  /** Offer minimum length of stay; overrides Redux-derived min when provided. */
  minNights?: number
  checkIn?: string
  checkOut?: string
  roomOption?: HotelRoomOption
  selectRoomOptionWhenAdded?: boolean
  isRoomOptionSelected?: boolean
  /** External gate (e.g. charter bundle flight price not yet resolved/bookable). */
  disabled?: boolean
  occupancyAgeThresholds?: AssistantOccupancyAgeThresholds
}

function AddHotelToTripButton({
  itineraryData,
  hotelName,
  offerId,
  offerType,
  hotelCity,
  contextStop,
  onSendMessage,
  variant = 'filled',
  size = 'small',
  fit,
  onAfterAction = noop,
  filterNights,
  minNights,
  checkIn,
  checkOut,
  roomOption,
  selectRoomOptionWhenAdded = false,
  isRoomOptionSelected = false,
  disabled = false,
  occupancyAgeThresholds,
}: Props) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()
  const {
    handleAddToTrip,
    handleSelectRoomOption,
    isModalOpen,
    closeModal,
    itineraryStatus,
    relevantStop,
    defaultDayNumber,
    defaultNights,
    minNights: resolvedMinNights,
    bookingRoomOption,
  } = useAddHotelToTrip({
    itineraryData,
    hotelName,
    offerId,
    offerType,
    hotelCity,
    contextStop,
    onSendMessage,
    onAfterAction,
    filterNights,
    minNights,
    checkIn,
    checkOut,
    roomOption,
    occupancyAgeThresholds,
  })

  const isAdded = itineraryStatus.type !== 'not-added'
  const shouldSelectRoomOption = isAdded && (selectRoomOptionWhenAdded || !!roomOption)
  const shouldShowSelectedRoomOption = shouldSelectRoomOption && isRoomOptionSelected

  const addToTripLabel = intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)
  const selectLabel = intl.formatMessage({
    id: 'n07_aCaZJpD6d2i0nsYg',
    defaultMessage: 'Add to trip',
    description: 'Button label to add a hotel room package to the trip planner itinerary',
  })
  const selectedLabel = intl.formatMessage({
    id: 'bVIhacFTytHhsK8sQjdK',
    defaultMessage: 'Already in trip',
    description: 'Disabled button label for a hotel room package already added to the trip planner itinerary',
  })

  const handleClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    if (shouldShowSelectedRoomOption) return
    if (shouldSelectRoomOption) {
      handleSelectRoomOption()
      return
    }
    handleAddToTrip()
  }, [handleAddToTrip, handleSelectRoomOption, shouldSelectRoomOption, shouldShowSelectedRoomOption])

  const handleModalSubmit = useCallback((message: string) => {
    showAddingToTripSnackbar(intl, hotelName)
    onSendMessage(message, { offerId, offerType, roomOption: bookingRoomOption, occupancyAgeThresholds })
    closeModal()
    onAfterAction()
  }, [intl, hotelName, onSendMessage, offerId, offerType, bookingRoomOption, occupancyAgeThresholds, closeModal, onAfterAction])

  const buttonLabel = shouldShowSelectedRoomOption ? selectedLabel : shouldSelectRoomOption ? selectLabel : isAdded ? itineraryStatus.label : addToTripLabel
  const startIcon = shouldShowSelectedRoomOption ? <LineCheckIcon /> : !isAdded || shouldSelectRoomOption ? <LinePlusIcon /> : undefined
  const endIcon = isAdded && !shouldSelectRoomOption ? <LineAngleDownIcon /> : undefined
  const isDisabled = isStreaming || shouldShowSelectedRoomOption || disabled

  const trigger = fit ? (
    <TextButton kind="secondary" size={size} fit={fit === 'full-width' ? 'full-width' : undefined} startIcon={startIcon} endIcon={endIcon} onClick={handleClick} disabled={isDisabled}>
      {buttonLabel}
    </TextButton>
  ) : (
    <AssistChip variant={variant} size={size} startIcon={startIcon} endIcon={endIcon} onClick={handleClick} disabled={isDisabled}>
      {buttonLabel}
    </AssistChip>
  )

  return (
    <>
      {trigger}
      {itineraryData && (
        <AddHotelToTripModal
          isOpen={isModalOpen}
          hotelName={hotelName}
          relevantStop={relevantStop}
          defaultDayNumber={defaultDayNumber}
          defaultNights={defaultNights}
          minNights={resolvedMinNights}
          onClose={closeModal}
          onSubmit={handleModalSubmit}
        />
      )}
    </>
  )
}

export default AddHotelToTripButton
